import MultiSlider, { LabelProps } from "@ptomasroos/react-native-multi-slider";
import React, { useMemo } from "react";

import { useTheme } from "@/providers/ThemeProvider";
import { ifWeb } from "@/utils/platform";

const getSliderConstants = (isDark: boolean) =>
  ({
    MARKER_SIZE: ifWeb(16, 16),
    MARKER_BG_COLOR: isDark ? "#e5e5e5" : "#1f1f1f",
    TRACK_COLOR: isDark ? "#4a4a4a" : "#c2bdc2",
    TRACK_HEIGHT: ifWeb(4, 4),
    TRACK_BORDER_RADIUS: ifWeb(8, 8),
    TRACK_SELECTED_COLOR: isDark ? "#e5e5e5" : "#1f1f1f",
    LOG_EPSILON: 1,
  }) as const;

/**
 *
 */
interface MultiSliderViewProps {
  value: number[];
  onChange: (value: [number, number]) => void;
  minValue: number;
  maxValue: number;
  step?: number;
  width?: number;
  renderCustomLabel?: React.ComponentType<LabelProps>;
  useLogScale?: boolean;
}

/**
 *
 */
interface BaseMultiSliderViewProps extends Omit<
  MultiSliderViewProps,
  "useLogScale"
> {
  transformValue: (val: number) => number;
  inverseTransformValue: (val: number) => number;
  getStepSize: () => number;
  handleValuesChange: (values: number[]) => void;
}

// Base component that handles common functionality
/**
 * Base multi-slider component with transform and step support
 * @param root0 - Component props
 * @param root0.value - Current slider values array
 * @param root0.transformValue - Function to transform values for display
 * @param root0.getStepSize - Function to get the step size for slider increments
 * @param root0.handleValuesChange - Callback invoked when slider values change
 * @param root0.minValue - Minimum allowed value
 * @param root0.maxValue - Maximum allowed value
 * @param root0.width - Width of the slider component
 * @param root0.renderCustomLabel - Optional custom label renderer component
 */
const BaseMultiSliderView: React.FC<BaseMultiSliderViewProps> = ({
  value,
  transformValue,
  getStepSize,
  handleValuesChange,
  minValue,
  maxValue,
  width,
  renderCustomLabel,
}) => {
  const { resolvedTheme } = useTheme();
  const SLIDER_CONSTANTS = useMemo(
    () => getSliderConstants(resolvedTheme === "dark"),
    [resolvedTheme]
  );

  const transformedValues = useMemo(
    () => value.map(transformValue),
    [value, transformValue]
  );

  const transformedBounds = useMemo(
    () => ({
      min: transformValue(minValue),
      max: transformValue(maxValue),
    }),
    [minValue, maxValue, transformValue]
  );

  const markerStyle = useMemo(
    () => ({
      height: SLIDER_CONSTANTS.MARKER_SIZE,
      width: SLIDER_CONSTANTS.MARKER_SIZE,
      backgroundColor: SLIDER_CONSTANTS.MARKER_BG_COLOR,
      borderWidth: 0,
      shadowColor: "#262626",
      shadowOffset: { width: -2, height: 2 },
      shadowOpacity: 0.2,
      shadowRadius: 4,
      elevation: 4,
    }),
    [SLIDER_CONSTANTS]
  );

  const trackStyle = useMemo(
    () => ({
      height: SLIDER_CONSTANTS.TRACK_HEIGHT,
      borderRadius: SLIDER_CONSTANTS.TRACK_BORDER_RADIUS,
      backgroundColor: SLIDER_CONSTANTS.TRACK_COLOR,
    }),
    [SLIDER_CONSTANTS]
  );

  const hasLabel = renderCustomLabel != null;

  return (
    <MultiSlider
      values={transformedValues}
      onValuesChangeFinish={handleValuesChange}
      sliderLength={width}
      min={transformedBounds.min}
      max={transformedBounds.max}
      step={getStepSize()}
      allowOverlap={false}
      enableLabel={hasLabel}
      markerStyle={markerStyle}
      selectedStyle={{ backgroundColor: SLIDER_CONSTANTS.TRACK_SELECTED_COLOR }}
      trackStyle={trackStyle}
      customLabel={renderCustomLabel}
    />
  );
};

export default BaseMultiSliderView;
